import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { PromoBannerConfig } from '@vastra/types';
import { usePromotions } from '../context';
import { normalizePromoBanners, selectActivePromoBanners, sanitizeHtml } from '@vastra/utils';

const variantStyles: Record<string, string> = {
  amber: 'bg-amber-950 text-amber-50 border-b border-amber-800/60',
  emerald: 'bg-emerald-900 text-emerald-50 border-b border-emerald-800/60',
  rose: 'bg-rose-900 text-rose-50 border-b border-rose-800/60',
  stone: 'bg-stone-800 text-stone-100 border-b border-stone-700/80',
  indigo: 'bg-indigo-900 text-indigo-50 border-b border-indigo-800/60',
  purple: 'bg-purple-900 text-purple-50 border-b border-purple-800/60',
  slate: 'bg-slate-800 text-slate-100 border-b border-slate-700/80',
  teal: 'bg-teal-900 text-teal-50 border-b border-teal-800/60',
  crimson: 'bg-red-700 text-white border-b border-red-600 shadow-lg shadow-red-500/20',
  gold: 'bg-yellow-600 text-yellow-50 border-b border-yellow-500 shadow-lg shadow-yellow-400/30',
  magenta: 'bg-pink-600 text-white border-b border-pink-500 shadow-lg shadow-pink-500/20',
  electric: 'bg-blue-600 text-white border-b border-blue-500 shadow-lg shadow-blue-400/30',
  neon: 'bg-lime-600 text-white border-b border-lime-500 shadow-lg shadow-lime-400/20',
  coral: 'bg-orange-600 text-white border-b border-orange-500 shadow-lg shadow-orange-400/30',
  scarlet: 'bg-red-600 text-white border-b border-red-500 shadow-lg shadow-red-500/25',
  violet: 'bg-violet-600 text-white border-b border-violet-500 shadow-lg shadow-violet-500/20',
  turquoise: 'bg-cyan-600 text-white border-b border-cyan-500 shadow-lg shadow-cyan-400/30',
  fuchsia: 'bg-fuchsia-600 text-white border-b border-fuchsia-500 shadow-lg shadow-fuchsia-500/25',
};

const sizeStyles: Record<
  NonNullable<PromoBannerConfig['fontSize']>,
  { container: string; prose: string; link: string }
> = {
  small: {
    container: 'py-2 text-xs',
    prose: 'text-xs prose-xs',
    link: 'text-[9px]',
  },
  medium: {
    container: 'py-2.5 text-sm',
    prose: 'text-sm prose-sm',
    link: 'text-[10px]',
  },
  large: {
    container: 'py-3 text-base',
    prose: 'text-base prose-base',
    link: 'text-xs',
  },
  xlarge: {
    container: 'py-3.5 text-[20px] leading-snug',
    prose: 'text-[20px] leading-snug prose-lg',
    link: 'text-sm',
  },
  '2xlarge': {
    container: 'py-4 text-[24px] leading-snug',
    prose: 'text-[24px] leading-snug prose-xl',
    link: 'text-base',
  },
};

function dismissStorageKey(banner: PromoBannerConfig): string {
  const id = banner.id?.trim();
  if (id) return `vastra_promo_banner_${id}`;
  const legacy = `${banner.message}|${banner.endsAt ?? ''}`;
  try {
    return `vastra_promo_banner_${btoa(unescape(encodeURIComponent(legacy))).slice(0, 48)}`;
  } catch {
    return `vastra_promo_banner_legacy`;
  }
}

const PromoBannerStrip: React.FC<{ banner: PromoBannerConfig }> = ({ banner }) => {
  const allowDismiss = banner.allowDismiss !== false;
  const dismissKey = useMemo(() => (allowDismiss ? dismissStorageKey(banner) : null), [banner, allowDismiss]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
    if (!dismissKey) return;
    try {
      if (window.localStorage.getItem(dismissKey) === '1') setDismissed(true);
    } catch {
      // ignore
    }
  }, [dismissKey]);

  const handleDismiss = useCallback(() => {
    if (!dismissKey) return;
    setDismissed(true);
    try {
      window.localStorage.setItem(dismissKey, '1');
    } catch {
      // ignore
    }
  }, [dismissKey]);

  if (allowDismiss && dismissed) return null;

  const variant = banner.variant ?? 'amber';
  const barClass = variantStyles[variant] ?? variantStyles.amber;
  const fontSize = banner.fontSize ?? 'medium';
  const sizeStyle = sizeStyles[fontSize] ?? sizeStyles.medium;

  return (
    <div className={`relative z-30 ${barClass}`} role="region" aria-label="Promotion">
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 ${sizeStyle.container} ${
          allowDismiss ? 'pr-12 sm:pr-14' : ''
        } flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-4 text-center font-medium`}
      >
        {banner.imageUrl && (
          <img
            src={banner.imageUrl}
            alt={banner.imageAlt || 'Promotion banner image'}
            className="h-12 w-auto shrink-0 mx-auto sm:mx-0"
          />
        )}
        <div
          className={`flex-1 prose prose-invert ${sizeStyle.prose} max-w-none [&_p]:m-0 [&_ul]:m-0 [&_ol]:m-0 [&_li]:m-0 [&_strong]:font-bold [&_em]:italic [&_u]:underline [&_a]:underline [&_a]:hover:opacity-75 [&_blockquote]:border-l-2 [&_blockquote]:pl-2 [&_blockquote]:italic [&_.ql-size-small]:text-xs [&_.ql-size-large]:text-base sm:[&_.ql-size-large]:text-lg [&_.ql-size-huge]:text-[20px] [&_.ql-size-huge]:leading-snug [&_.ql-size-2xlarge]:text-[24px] [&_.ql-size-2xlarge]:leading-snug`}
        >
          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner.message) }} />
        </div>
        {banner.linkUrl && banner.linkLabel && (
          <Link
            to={banner.linkUrl}
            className={`inline-flex items-center justify-center shrink-0 ${sizeStyle.link} font-bold uppercase tracking-[0.2em] underline underline-offset-4 hover:opacity-90`}
          >
            {banner.linkLabel}
          </Link>
        )}
      </div>
      {allowDismiss && (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-white/10 transition-colors"
          aria-label="Dismiss promotion"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * Dismissible seasonal / campaign strips below the navbar.
 * Shows up to two live banners targeted to the current page; dismiss state is keyed by each `banner.id`.
 */
const PromoBanner: React.FC = () => {
  const { payload } = usePromotions();
  const location = useLocation();

  const activeBanners = useMemo(() => {
    const list = normalizePromoBanners(payload);
    return selectActivePromoBanners(list, payload.maxActiveBanners ?? 2, location.pathname);
  }, [payload, location.pathname]);

  if (!activeBanners.length) return null;

  return (
    <>
      {activeBanners.map((banner, index) => (
        <PromoBannerStrip key={banner.id ?? `banner-${index}`} banner={banner} />
      ))}
    </>
  );
};

export default PromoBanner;
